"use client";

import { useT } from "@/lib/i18n/context";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import { wDedicatedJobOffersTranslations } from "./WDedicatedJobOffers.i18n";
import styles from "./WDedicatedJobOffers.module.css";

/**
 * WDedicatedJobOffers - widget listy dedykowanych ofert pracy.
 *
 * Karty ofert z pigulkami meta + opisem + pigulkami technologii + przyciskiem
 * "Aplikuj" (URL/mailto per oferta, z pola `apply_href` w tabeli).
 *
 * Dane z `W_DEDICATED_JOB_OFFERS_CONTROLLER::getApiContent()` - widget czyta
 * z komponentu `c_dedicated_job_offers` calej domeny+jezyka.
 */
export type WDedicatedJobOffersItem = {
  id: number;
  name: string;
  pills: string[];
  description: string | null;
  technologies: string[];
  apply_label: string | null;
  apply_href: string | null;
};

export type WDedicatedJobOffersContent = {
  symbol: string;
  window_id: number;
  heading: string | null;
  items: WDedicatedJobOffersItem[];
  cta: {
    heading: string;
    text: string | null;
    button_label: string | null;
    apply_href: string | null;
  } | null;
};

export type WDedicatedJobOffersProps = {
  content: WDedicatedJobOffersContent | null;
};

function normalizeLines(raw: unknown): string[] {
  if (Array.isArray(raw))
    return raw.filter((s): s is string => typeof s === "string" && s.trim() !== "").map((s) => s.trim());
  if (typeof raw === "string")
    return raw.split(/\r\n|\r|\n/).map((s) => s.trim()).filter((s) => s !== "");
  return [];
}

export function WDedicatedJobOffers({ content }: WDedicatedJobOffersProps) {
  const t = useT(wDedicatedJobOffersTranslations);

  if (!content || (content.items.length === 0 && !content.cta)) return null;

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        {content.heading && (
          <h2 className={styles.heading}>{content.heading}</h2>
        )}

        <ul className={styles.list}>
          {content.items.map((item) => (
            <JobCard key={item.id} item={item} fallbackLabel={t.apply} />
          ))}

          {content.cta && (
            <li className={styles.ctaCard}>
              <div className={styles.body}>
                <h3 className={styles.ctaHeading}>{content.cta.heading}</h3>
                {content.cta.text && (
                  <p className={styles.cardDesc}>{content.cta.text}</p>
                )}
              </div>

              {content.cta.apply_href && (
                <div className={styles.applyWrap}>
                  <a href={content.cta.apply_href} className={styles.ctaBtn}>
                    {content.cta.button_label ?? t.sendCv}
                  </a>
                </div>
              )}
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}

function JobCard({ item, fallbackLabel }: { item: WDedicatedJobOffersItem; fallbackLabel: string }) {
  const pills = normalizeLines(item.pills);
  const techs = normalizeLines(item.technologies);
  const internal = item.apply_href ? extractInternalHref(item.apply_href) : null;
  const label = item.apply_label && item.apply_label.trim() !== "" ? item.apply_label : fallbackLabel;
  const showButton = !!item.apply_href;

  return (
    <li className={styles.card}>
      <div className={styles.body}>
        <h3 className={styles.cardTitle}>{item.name}</h3>

        {pills.length > 0 && (
          <ul className={styles.pillList}>
            {pills.map((p, i) => (
              <li key={i} className={styles.pill}>
                {p}
              </li>
            ))}
          </ul>
        )}

        {item.description && (
          <p className={styles.cardDesc}>{item.description}</p>
        )}

        {techs.length > 0 && (
          <ul className={styles.techList}>
            {techs.map((t, i) => (
              <li key={i} className={styles.tech}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showButton && (
        <div className={styles.applyWrap}>
          {internal !== null ? (
            <Link href={internal} className={styles.applyBtn}>
              {label} <span aria-hidden="true">&#8594;</span>
            </Link>
          ) : (
            <a
              href={item.apply_href!}
              className={styles.applyBtn}
              target={item.apply_href!.startsWith("mailto:") ? undefined : "_blank"}
              rel={item.apply_href!.startsWith("mailto:") ? undefined : "noopener noreferrer"}
            >
              {label} <span aria-hidden="true">&#8594;</span>
            </a>
          )}
        </div>
      )}
    </li>
  );
}
